import { createModel } from "hox";
import React, { Key, useState } from "react";
import { menuService } from "./MenuService";
import { message, TablePaginationConfig } from "antd";
import { IMdMenuDetail, IMdMenuFields, MenuActionType } from "./MenuTypes";
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE } from "@src/utils/constants";
import { EditModeType, ISearchPage, OptionDsType } from "@src/types/FmsTypes";
import { transPgToPagination } from "@src/utils/utils";
import { appService } from "../../app/AppService";
import { resourceService } from "../resource/ResourceService";
import { IMdResourceDetail, IMdResourceSearch } from "../resource/ResourceTypes";

export type MenuOptMode = 'add_root' | 'add' | 'detail';

interface ITreeDs<T> {
    enable?: number;
    code?: string;
    children?: T[]
}

export interface ITreeData<T> {
    key: Key;
    title?: string;
    value?: Key;
    children?: ITreeData<T>[] | undefined;
}

export interface IFieldValue<T> {
    [key: string]: T[keyof T]
}


export function trasDsToTreeData<T extends ITreeDs<T>>(
    ds: T[],
    keyField: keyof T,
    titleField: keyof T,
): ITreeData<T>[] {
    return ds.map(el => {
        const key = el[keyField] as any;
        const title = el[titleField] as any;
        const children = el.children && el.children.length > 0 ? trasDsToTreeData(el.children, keyField, titleField) : undefined;
        const disabled = el.enable === 0;
        return {
            el,
            key,
            title,
            disabled,
            value: el["code"],
            selectable: !disabled,
            children,
        }
    });
}

export function filterTree<T extends ITreeDs<T>>(arg: T[], code: Key): T | undefined {
    for (let i = 0; i < arg.length; i++) {
        const el = arg[i]
        if (el.code && el.code === code) {
            return el;
        }
        if (el.children && el.children.length > 0) {
            const ret = filterTree(el.children, code)
            if (ret) return ret;
        }
    }
}



export const useMenuModel = createModel(function () {
    const [menuOptMode, setMenuOptMode] = useState<MenuOptMode>("add")
    const initDataSource: IMdResourceDetail[] = [];
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }

    //列表数据源
    const [dataSource, setDataSource] = useState<IMdResourceDetail[]>(initDataSource);
    const [appOptions, setAppOptions] = useState<OptionDsType[]>()
    const [appCode, setAppCode] = useState<string>('')
    const [treeData, setTreeData] = useState<ITreeData<IMdMenuDetail>[]>()

    /** 菜单部分 */
    const [selectedMenuKeys, setSelectedMenuKeys] = useState<React.Key[]>([]);
    const [selectedMenu, setSelectedMenu] = useState<IMdMenuDetail>();
    const initFormValues = {};
    const [formValues, setFormValues] = useState<IMdMenuFields>(initFormValues);

    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);

    const [selectParentData, setSelectParentData] = useState<IMdMenuDetail>()

    const [actionType, setActionType] = useState<MenuActionType>('menu')

    /** 资源部分 */
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedData, setSelectedData] = useState<IMdResourceDetail>();
    const [resourceModel, setResourceModel] = useState<EditModeType>('add')
    const [drawerVisible, setDrawerVisible] = useState(false);




    /**
     * 查询应用列表
     * @param callBack 
     */
    const loadApp = async (callBack?: (e: OptionDsType[]) => void) => {
        const appOption = await appService.find({})
        setAppOptions(appOption)
        callBack?.(appOption)
    }

    /**
     * 根据应用code查找菜单树
     */
    const loadMenuTreeByApp = async (code?: string) => {
        const { retData } = await menuService.findTreeByApp(code ?? appCode)
        // debugger
        // if (selectedMenuKeys.length > 0) {
        //     let selectedNode = retData.find(item => item.code === selectedMenu?.code)
        //     setSelectedMenu(selectedNode)
        //     setFormValues(selectedNode as IMdMenuDetail)
        // }
        setTreeData(retData)
    }


    const loadTableData = async (page?: ISearchPage) => {
        const searchParams: IMdResourceSearch = {
            page: {
                pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
                pageNum: page?.pageNum ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
            },
            menuCode: selectedMenu?.code,
            appCode: appCode
        }
        let { retData } = await resourceService.page(searchParams);
        const { records, ...pg } = retData;
        setPgCfg(transPgToPagination(pg));
        setDataSource(records);
    }

    const syncData = async () => {
        let ret = await menuService.sync({});
        ret ? message.success("同步成功!重新加载数据...") : message.error("同步失败");
        ret && await loadMenuTreeByApp()
    }

    const updateFormValue = (value: IFieldValue<IMdMenuFields>) => {
        setFormValues({ ...formValues, ...value });
    }

    return {
        menuOptMode,
        /***field***/
        dataSource,
        selectedRowKeys,
        selectedMenuKeys,
        formValues,
        selectedData,
        selectedMenu,

        selectParentData,
        actionType,

        pgCfg,
        drawerVisible,
        appOptions,
        treeData,
        appCode,
        resourceModel,

        /***method***/
        setDataSource,
        setSelectedRowKeys,
        setSelectedMenuKeys,
        loadApp,
        loadMenuTreeByApp,
        syncData,
        setFormValues,
        updateFormValue,
        setMenuOptMode,
        setSelectedData,
        setSelectedMenu,

        setSelectParentData,
        setActionType,
        loadTableData,
        setDrawerVisible,
        setAppCode,
        setResourceModel,
    }
});
